import { getModeFormatDate, getYearBounds } from '@gripp/shared-logic';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, Pressable, Text } from 'react-native';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import { tokens } from '../../crop-circle/tokens';
import { useFormContext } from '../formContext';
import { DateRangeSelectorProps } from './dateRangeSelector';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

type DatePickerWheelProps = DateRangeSelectorProps & {
  onDateSelected: (...event: any[]) => void;
  isStartDate?: boolean;
  isVisible: boolean;
  onClose: () => void;
  mode?: 'date' | 'time' | 'datetime';
  name?: string;
  minDate?: string;
  pickerContainerStyleIOS?: object;
  modalStyleIOS?: object;
  confirmButtonStyleIOS?: object;
};

export const DatePickerWheel: React.FC<DatePickerWheelProps> = (props) => {
  const { t } = useTranslation();
  const {
    control,
    onDateSelected,
    isStartDate,
    setValue,
    isVisible,
    onClose,
    mode,
    name,
  } = props;
  const today = moment();
  const minMoment = props.minDate
    ? moment(props.minDate, 'YYYY-MM-DD', true).startOf('day')
    : null;
  const formatDate = getModeFormatDate(props.mode || 'date');

  const initialMoment =
    minMoment && minMoment.isAfter(today) ? minMoment : today;
  const [currentDate, setCurrentDate] = useState<Date>(initialMoment.toDate());
  const fieldStartKey =
    name && mode === 'time'
      ? name.replace('endTime', 'startTime')
      : 'startDate';
  const fieldEndKey =
    name && mode === 'time' ? name.replace('startTime', 'endTime') : 'endDate';
  const formContext = useFormContext();
  const getValues = formContext?.getValues || (() => ({}));

  const handleConfirm = useCallback(
    (date: Date) => {
      if (!date || (props?.minDate && moment(date).isBefore(props.minDate))) {
        return;
      }

      const newDate = moment(date);
      if (isStartDate) {
        onDateSelected(
          props.mode === 'time'
            ? newDate.toISOString()
            : newDate.format(formatDate)
        );
        setCurrentDate(newDate.toDate());
        const startDate = moment(date);
        const endDate = moment(getValues(fieldEndKey));

        if (startDate.isAfter(endDate)) setValue(fieldEndKey, '');

        onClose();
        return;
      }
      if (name?.includes('endTime') || name?.includes('endDate')) {
        const startDate = moment(getValues(fieldStartKey));

        if (startDate.isAfter(newDate)) return;
      }

      onDateSelected(
        props.mode === 'time'
          ? newDate.toISOString()
          : newDate.format(formatDate)
      );
      setCurrentDate(newDate.toDate());
      onClose();
    },
    [isStartDate, onDateSelected, control, setValue, onClose, props.mode]
  );

  const { firstDay, lastDay } = getYearBounds();
  const minDateObj = props.minDate
    ? moment(props.minDate, 'YYYY-MM-DD').startOf('day').toDate()
    : firstDay;

  return (
    <DateTimePickerModal
      isVisible={isVisible}
      mode={props.mode ?? 'date'}
      onConfirm={handleConfirm}
      customConfirmButtonIOS={({ onPress }) => (
        <Pressable
          onPress={onPress}
          style={[cropCircleStyles.confirmButton, props.confirmButtonStyleIOS]}
        >
          <Text style={cropCircleStyles.confirmLabel}>
            {t('alerts.confirm')}
          </Text>
        </Pressable>
      )}
      onCancel={onClose}
      buttonTextColorIOS={tokens.colors.brand.black}
      maximumDate={lastDay}
      minimumDate={minDateObj}
      date={currentDate}
      display={'spinner'}
      negativeButton={{ textColor: tokens.colors.brand.black }}
      positiveButton={{ textColor: tokens.colors.brand.black }}
      key={currentDate.toISOString()}
      pickerContainerStyleIOS={
        props.pickerContainerStyleIOS
          ? {
              justifyContent: 'center',
              alignItems: 'center',
              ...cropCircleStyles.pickerContainer,
              ...(props.pickerContainerStyleIOS as any),
            }
          : {
              justifyContent: 'center',
              alignItems: 'center',
              ...cropCircleStyles.pickerContainer,
            }
      }
      modalStyleIOS={
        props.modalStyleIOS
          ? { ...cropCircleStyles.modal, ...(props.modalStyleIOS as any) }
          : cropCircleStyles.modal
      }
    />
  );
};

const screenHeight = Dimensions.get('window').height;

const cropCircleStyles = themedStyleSheet((role) => ({
  pickerContainer: {
    backgroundColor: role.canvas,
    paddingHorizontal: tokens.spacing.lg,
  },
  modal: {
    backgroundColor: role.canvas,
    justifyContent: 'flex-end',
    alignItems: 'stretch',
    margin: 0,
    padding: 0,
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: screenHeight * 0.5, // Limit to half screen height for bottom sheet
  },
  confirmButton: {
    height: 57,
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: role.surfaceHover,
    borderTopWidth: 0.5,
    borderTopColor: role.borderSubtle,
  },
  confirmLabel: {
    ...tokens.typography.body.medium,
    fontWeight: '500',
    color: role.textStrong,
  },
}));
